import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TABLE_SEARCH_DEBOUNCE_MS } from "../ExpandableTableSearch/ExpandableTableSearch";

export type FilterSearchFieldProps = {
  value: string;
  onApply: (next: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  minSearchLength?: number;
};

const DEFAULT_MIN_LEN = 2;

export default function FilterSearchField({
  value,
  onApply,
  label,
  placeholder,
  disabled = false,
  minSearchLength = DEFAULT_MIN_LEN,
}: FilterSearchFieldProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [helper, setHelper] = useState<string | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const validate = useCallback(
    (raw: string) => {
      const query = raw.trim();
      if (query.length > 0 && query.length < minSearchLength) {
        setHelper(t("common.typeAtLeast2"));
        return false;
      }
      setHelper(null);
      return true;
    },
    [minSearchLength, t],
  );

  useEffect(() => {
    if (disabled) return;
    const timer = window.setTimeout(() => {
      const query = draftRef.current.trim();
      if (query.length < minSearchLength) return;
      if (!validate(draftRef.current)) return;
      if (query !== valueRef.current.trim()) {
        onApply(query);
      }
    }, TABLE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [disabled, draft, minSearchLength, onApply, validate]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const query = draft.trim();
    if (query.length === 0) {
      setHelper(null);
      onApply("");
      return;
    }
    if (!validate(draft)) return;
    onApply(query);
  };

  return (
    <TextField
      size="small"
      fullWidth
      label={label ?? t("common.search")}
      placeholder={placeholder ?? t("common.searchPlaceholder")}
      value={draft}
      disabled={disabled}
      error={Boolean(helper)}
      helperText={helper || undefined}
      onChange={(event) => {
        setDraft(event.target.value);
        validate(event.target.value);
      }}
      onKeyDown={handleKeyDown}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} aria-hidden />
          </InputAdornment>
        ),
        endAdornment:
          draft.length > 0 ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={t("common.clear")}
                edge="end"
                onClick={() => {
                  setDraft("");
                  setHelper(null);
                  onApply("");
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
      }}
      sx={{ "& .MuiOutlinedInput-root": { pr: 0.5 } }}
    />
  );
}
